import { useEffect, useRef } from 'react'
import type { VoiceShape } from '../../types/jobs'
import type { Job, JobStep } from '../../types/voice'

// The scene in the console: mail streaming out of the mailbox, through the stage that is working,
// into the profile. Packets move at the measured rate (clamped so a fast file still reads as a
// stream and a slow live mailbox still visibly moves); the working stage glows; landed stages tick.
// Canvas, tokens only (colours are read from CSS variables at mount), pauses when hidden.

const STAGES = ['voice-sync', 'voice-index', 'voice-scenarios', 'voice-exemplars', 'voice-describe', 'voice-verify']
const SHORT: Record<string, string> = { 'voice-sync': 'read', 'voice-index': 'measure', 'voice-scenarios': 'sort', 'voice-exemplars': 'choose', 'voice-describe': 'describe', 'voice-verify': 'check' }

interface Packet { t: number; lane: number; speed: number; hue: number }

export function Pipeline({ job, current, shape }: { job: Job; current: JobStep | null; shape: VoiceShape | null }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const live = useRef({ job, current, shape })
  live.current = { job, current, shape }

  useEffect(() => {
    const el = canvas.current
    if (!el) return
    const ctx = el.getContext('2d')
    if (!ctx) return
    const css = getComputedStyle(el)
    const colour = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback
    const glow = colour('--radar-glow', '#2ee6cf')
    const glowSoft = colour('--radar-glow-soft', 'rgba(46,230,207,0.22)')
    const line = colour('--radar-line-strong', 'rgba(15,158,142,0.55)')
    const lineFaint = colour('--radar-line', 'rgba(15,158,142,0.22)')
    const text = colour('--radar-text', '#c8f5ee')
    const dim = colour('--radar-text-dim', 'rgba(200,245,238,0.55)')
    const warm = colour('--radar-blip-warm', '#ffb547')
    const mono = colour('--font-mono', 'monospace')

    let packets: Packet[] = []
    let raf = 0
    let last = performance.now()
    let spawn = 0
    let dpr = 1
    let w = 0
    let h = 0

    const size = () => {
      dpr = Math.min(2, window.devicePixelRatio || 1)
      const rect = el.getBoundingClientRect()
      w = Math.max(1, Math.round(rect.width))
      h = Math.max(1, Math.round(rect.height))
      el.width = Math.round(w * dpr)
      el.height = Math.round(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    size()
    const ro = new ResizeObserver(size)
    ro.observe(el)

    const frame = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000)
      last = now
      const { job, current } = live.current
      const terminal = job.status === 'Completed' || job.status === 'Failed' || job.status === 'Cancelled'
      const stageIdx = current ? STAGES.indexOf(current.stepId) : terminal ? STAGES.length : -1
      const rate = current?.rate ?? 0
      // packets per second on screen: a readable stream regardless of the true rate
      const pps = terminal ? 0 : current ? Math.max(3, Math.min(28, 3 + Math.log10(1 + rate) * 9)) : 1.5
      spawn += dt * pps
      while (spawn >= 1) {
        spawn -= 1
        packets.push({ t: 0, lane: (Math.random() - 0.5) * 0.9, speed: 0.55 + Math.random() * 0.35, hue: Math.random() < 0.12 ? 1 : 0 })
      }
      for (const p of packets) p.t += dt * p.speed
      packets = packets.filter((p) => p.t < 1.05)

      ctx.clearRect(0, 0, w, h)
      const padX = 56
      const y = h * 0.5
      const x0 = padX
      const x1 = w - padX
      const span = x1 - x0
      const stageX = (i: number) => x0 + (span * (i + 0.5)) / STAGES.length

      // the rail
      ctx.strokeStyle = lineFaint
      ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x0 - 30, y); ctx.lineTo(x1 + 30, y); ctx.stroke()
      ctx.setLineDash([2, 6])
      ctx.beginPath(); ctx.moveTo(x0 - 30, y - 14); ctx.lineTo(x1 + 30, y - 14); ctx.moveTo(x0 - 30, y + 14); ctx.lineTo(x1 + 30, y + 14); ctx.stroke()
      ctx.setLineDash([])

      // source and sink
      ctx.font = `10px ${mono}`
      ctx.textAlign = 'center'
      ctx.fillStyle = dim
      drawEnvelope(ctx, x0 - 34, y, 22, line)
      ctx.fillText('MAILBOX', x0 - 34, y + 30)
      drawProfile(ctx, x1 + 34, y, 22, terminal && job.status === 'Completed' ? glow : line)
      ctx.fillText('PROFILE', x1 + 34, y + 30)

      // stages
      STAGES.forEach((id, i) => {
        const sx = stageX(i)
        const step = job.steps.find((s) => s.stepId === id)
        const done = step?.status === 'Completed'
        const active = i === stageIdx && !terminal
        const failed = step?.status === 'Failed'
        ctx.beginPath()
        ctx.arc(sx, y, active ? 11 : 8, 0, Math.PI * 2)
        if (active) {
          const pulse = 0.55 + 0.45 * Math.sin(now / 260)
          ctx.shadowColor = glow; ctx.shadowBlur = 18 * pulse
          ctx.fillStyle = glow
          ctx.fill()
          ctx.shadowBlur = 0
          ctx.strokeStyle = glowSoft; ctx.lineWidth = 6; ctx.stroke()
        } else if (failed) {
          ctx.fillStyle = warm; ctx.fill()
        } else if (done) {
          ctx.fillStyle = glow; ctx.globalAlpha = 0.85; ctx.fill(); ctx.globalAlpha = 1
        } else {
          ctx.fillStyle = 'transparent'; ctx.strokeStyle = line; ctx.lineWidth = 1.2; ctx.stroke()
        }
        ctx.fillStyle = active ? text : dim
        ctx.fillText(SHORT[id].toUpperCase(), sx, y - 22)
        if (step && (active || done) && step.total) {
          ctx.fillStyle = dim
          ctx.fillText(`${step.done}/${step.total}`, sx, y + 30)
        } else if (step && done) {
          ctx.fillStyle = dim
          ctx.fillText('✓', sx, y + 30)
        }
      })

      // packets travel from the mailbox to the working stage (or the profile when landing)
      const endX = stageIdx < 0 ? x0 : stageIdx >= STAGES.length ? x1 + 34 : stageX(stageIdx)
      const startX = stageIdx <= 0 ? x0 - 34 : stageX(Math.max(0, stageIdx - 1))
      for (const p of packets) {
        const px = startX + (endX - startX) * p.t
        const py = y + p.lane * 10
        const a = p.t < 0.1 ? p.t / 0.1 : p.t > 0.85 ? Math.max(0, (1.05 - p.t) / 0.2) : 1
        ctx.globalAlpha = a * 0.9
        ctx.fillStyle = p.hue ? warm : glow
        ctx.fillRect(px - 5, py - 1.5, 10, 3)
        ctx.globalAlpha = 1
      }

      // caption
      ctx.textAlign = 'left'
      ctx.fillStyle = dim
      ctx.font = `10px ${mono}`
      const caption = terminal
        ? job.status === 'Completed' ? 'PROFILE LANDED' : job.status.toUpperCase()
        : current ? `${SHORT[current.stepId]?.toUpperCase() ?? 'WORK'}${current.rate ? ` · ${current.rate >= 10 ? current.rate.toFixed(0) : current.rate.toFixed(1)}/S` : ''}` : 'QUEUED'
      ctx.fillText(caption, 12, 16)
      ctx.textAlign = 'right'
      ctx.fillText(`${job.steps.filter((s) => s.status === 'Completed').length}/${job.steps.length} STAGES`, w - 12, 16)

      if (!document.hidden) raf = requestAnimationFrame(frame)
    }
    const onVis = () => { if (!document.hidden) { last = performance.now(); raf = requestAnimationFrame(frame) } }
    document.addEventListener('visibilitychange', onVis)
    raf = requestAnimationFrame(frame)
    return () => { cancelAnimationFrame(raf); ro.disconnect(); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  return <canvas ref={canvas} className="pipeline" aria-hidden="true" />
}

function drawEnvelope(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, stroke: string) {
  ctx.strokeStyle = stroke; ctx.lineWidth = 1.2
  ctx.strokeRect(x - s / 2, y - s * 0.32, s, s * 0.64)
  ctx.beginPath(); ctx.moveTo(x - s / 2, y - s * 0.32); ctx.lineTo(x, y + s * 0.05); ctx.lineTo(x + s / 2, y - s * 0.32); ctx.stroke()
}

function drawProfile(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, stroke: string) {
  ctx.strokeStyle = stroke; ctx.lineWidth = 1.2
  ctx.beginPath(); ctx.arc(x, y - s * 0.18, s * 0.2, 0, Math.PI * 2); ctx.stroke()
  ctx.beginPath(); ctx.arc(x, y + s * 0.42, s * 0.42, Math.PI, Math.PI * 2); ctx.stroke()
  ctx.beginPath(); ctx.arc(x, y, s * 0.62, 0, Math.PI * 2); ctx.stroke()
}
